import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { COUNTRIES, COUNTRIES_MAP, flagEmoji, countryName } from "@/lib/countries";
import { useI18n } from "@/i18n/i18n";
import { useMemo } from "react";

/**
 * CountrySelect — full-country dropdown. Stores ISO-2 code (e.g. "US").
 * Use empty string "" to indicate "no country".
 */
export function CountrySelect({ value, onChange, testId }) {
  const { t, lang } = useI18n();
  const SENTINEL = "__none__";
  return (
    <Select
      value={value || SENTINEL}
      onValueChange={(v) => onChange(v === SENTINEL ? "" : v)}
    >
      <SelectTrigger data-testid={testId}>
        <SelectValue placeholder={t("select_country")} />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        <SelectItem value={SENTINEL}>{t("no_country")}</SelectItem>
        {COUNTRIES.map((c) => (
          <SelectItem key={c.code} value={c.code}>
            <span className="mr-1.5">{flagEmoji(c.code)}</span>
            {countryName(c.code, lang)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * WhatsAppInput — dial-code dropdown (flag + +code) + free-text number input.
 *
 * Stored format: "<dial><space><digits>" → e.g. "+506 70768283".
 * If the saved string doesn't start with a recognized dial code, the number
 * is shown raw and the dial defaults to `defaultCountry`.
 */
export function WhatsAppInput({ value, onChange, defaultCountry, testIdDial, testIdNumber }) {
  const { t } = useI18n();
  const { dialCode, digits } = useMemo(() => parseWhatsApp(value, defaultCountry), [value, defaultCountry]);

  const handleDialChange = (newDial) => {
    const next = (newDial + (digits ? ` ${digits}` : "")).trim();
    onChange(next || null);
  };
  const handleDigitsChange = (newDigits) => {
    const cleaned = newDigits.replace(/[^\d]/g, "");
    const next = (dialCode + (cleaned ? ` ${cleaned}` : "")).trim();
    onChange(next || null);
  };

  // Deduplicate dial codes (some countries share +1, +7, etc.) keeping the
  // representative country (US for +1, RU for +7, KZ already deduped, etc.).
  const dialOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const c of COUNTRIES) {
      const key = `${c.dial}|${c.code}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(c);
    }
    return out;
  }, []);

  return (
    <div className="flex gap-1.5">
      <Select value={dialCode} onValueChange={handleDialChange}>
        <SelectTrigger data-testid={testIdDial} className="w-[120px] shrink-0">
          <SelectValue>
            <span className="mr-1">{flagEmoji(findCountryByDial(dialCode))}</span>
            {dialCode}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {dialOptions.map((c) => (
            <SelectItem key={`${c.code}-${c.dial}`} value={c.dial}>
              <span className="mr-1.5">{flagEmoji(c.code)}</span>
              {c.dial} · <span className="text-slate-500 text-xs ml-1">{c.name_es}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        data-testid={testIdNumber}
        type="tel"
        inputMode="numeric"
        value={digits}
        onChange={(e) => handleDigitsChange(e.target.value)}
        placeholder={t("whatsapp_number_placeholder")}
        className="flex-1"
      />
    </div>
  );
}

/** Parses a stored WhatsApp string into { dialCode, digits } using COUNTRIES dial codes. */
export function parseWhatsApp(stored, defaultCountry = "US") {
  const fallbackDial = COUNTRIES_MAP[defaultCountry]?.dial || "+1";
  if (!stored) return { dialCode: fallbackDial, digits: "" };
  const raw = String(stored).trim();
  if (!raw.startsWith("+")) {
    // legacy free-text number — keep digits, default dial
    return { dialCode: fallbackDial, digits: raw.replace(/[^\d]/g, "") };
  }
  // Sort dials by length desc to match the longest prefix first
  // (e.g. "+1268" before "+1").
  const dials = [...new Set(COUNTRIES.map((c) => c.dial))].sort((a, b) => b.length - a.length);
  for (const d of dials) {
    if (raw.startsWith(d)) {
      const rest = raw.slice(d.length).replace(/[^\d]/g, "");
      return { dialCode: d, digits: rest };
    }
  }
  return { dialCode: fallbackDial, digits: raw.replace(/[^\d]/g, "") };
}

function findCountryByDial(dial) {
  const c = COUNTRIES.find((x) => x.dial === dial);
  return c?.code || "US";
}
